/**
 * Quote money math — single source of truth for the B2B quote builder.
 *
 * VAT RULE (matches api/order.php): VAT is charged on the FULL taxable base,
 * i.e. products AND shipping. Shipping is part of the taxable supply under
 * Finnish VAT law — it is not a VAT-free pass-through.
 *
 *   subtotal  = Σ (unit × qty × (1 − discount%))
 *   netTotal  = subtotal + shipping
 *   vatAmount = netTotal × vatRate
 *   total     = netTotal + vatAmount
 */

export const DEFAULT_VAT_RATE = 25.5;

/** VAT presets a salesperson actually needs. */
export const VAT_PRESETS = [
  { value: 25.5, label: '25.5% — Finland (standard)' },
  { value: 14,   label: '14% — Reduced' },
  { value: 10,   label: '10% — Reduced' },
  { value: 0,    label: '0% — Reverse charge / Intra-EU / Export' },
];

export const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

/** Net line total after the per-line discount. */
export function lineNet(line) {
  const unit = Number(line?.price) || 0;
  const qty = Math.max(0, Number(line?.qty) || 0);
  const disc = Math.min(100, Math.max(0, Number(line?.discountPct) || 0));
  return round2(unit * qty * (1 - disc / 100));
}

/** Gross line total before discount — used to show the strike-through amount. */
export function lineGross(line) {
  const unit = Number(line?.price) || 0;
  const qty = Math.max(0, Number(line?.qty) || 0);
  return round2(unit * qty);
}

/**
 * @returns {{subtotal:number, discountTotal:number, shipping:number,
 *            netTotal:number, vatRate:number, vatAmount:number, total:number,
 *            totalQty:number, totalWeight:number}}
 */
export function computeTotals(lines = [], shippingCost = 0, vatRate = DEFAULT_VAT_RATE) {
  const subtotal = round2(lines.reduce((s, l) => s + lineNet(l), 0));
  const gross = round2(lines.reduce((s, l) => s + lineGross(l), 0));
  const discountTotal = round2(gross - subtotal);
  const shipping = round2(shippingCost);

  // The fix: VAT base includes shipping.
  const netTotal = round2(subtotal + shipping);
  const rate = Math.max(0, Number(vatRate) || 0);
  const vatAmount = round2(netTotal * (rate / 100));
  const total = round2(netTotal + vatAmount);

  const totalQty = lines.reduce((s, l) => s + (Number(l.qty) || 0), 0);
  const totalWeight = round2(
    lines.reduce((s, l) => s + (Number(l.weight) || 0) * (Number(l.qty) || 0), 0)
  );

  return { subtotal, discountTotal, shipping, netTotal, vatRate: rate, vatAmount, total, totalQty, totalWeight };
}

export const money = (n) =>
  '€' + (Number(n) || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** "1 234.56 EUR" — the format used on the printed invoice. */
export const eur = (n) =>
  (Number(n) || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' EUR';

/** Unit price after the line's discount (VAT excluded). */
export const effectiveUnit = (line) => {
  const unit = Number(line?.price) || 0;
  const disc = Math.min(100, Math.max(0, Number(line?.discountPct) || 0));
  return unit * (1 - disc / 100);
};

/**
 * Invoice column math. The reference invoice rounds the *line* total from the
 * raw product (not from the already-rounded unit-incl-VAT), e.g.
 *   2 × 29.40 × 1.255 = 73.794 → 73.79   (NOT 2 × 36.90 = 73.80)
 * so we mirror that here.
 */
export const unitInclVat = (line, vatRate) => round2(effectiveUnit(line) * (1 + (Number(vatRate) || 0) / 100));
export const lineTotalInclVat = (line, vatRate) =>
  round2(effectiveUnit(line) * (Number(line?.qty) || 0) * (1 + (Number(vatRate) || 0) / 100));

/** Q-YYYY-#### using a per-year counter kept in localStorage. */
export function nextQuoteNumber() {
  const year = new Date().getFullYear();
  const key = `elnaf_quote_seq_${year}`;
  let seq = 1;
  try {
    seq = parseInt(localStorage.getItem(key) || '0', 10) + 1;
    localStorage.setItem(key, String(seq));
  } catch { /* private mode — fall back to 1 */ }
  return `Q-${year}-${String(seq).padStart(4, '0')}`;
}
